import { useEffect, useState } from 'react';

import { useCdpContext } from 'state/cdp';
import { CustomEventNames, EventTypes } from 'state/cdp/constants';
import { getConfigValue } from 'utils/environment';

declare global {
  interface Window {
    OneTrust: any;
    OptanonWrapper: any;
  }
}

type ErrorState = ErrorEvent | null;

const IS_BROWSER = typeof window !== 'undefined' && typeof window.document !== 'undefined';
const ONE_TRUST_SDK_SCRIPT_ID = 'one-trust-cookie-consent-sdk';
const getAutoBlockScript = (scriptId: string) => {
  return `https://cdn.cookielaw.org/consent/${scriptId}/OtAutoBlock.js`;
};

/**
 * Loads one trust sdk cookie consent script
 */
export default function useOneTrustSdk() {
  const [loading, setLoading] = useState(true);
  const [loaded, setLoaded] = useState(false);
  const [error, setError] = useState<ErrorState>(null);
  const [cookieButtonName, setCookieButtonName] = useState(null);
  const { trackEvent } = useCdpContext();

  const acceptAllCookiesButton = document.getElementById('onetrust-accept-btn-handler');
  const rejectAllButton = document.getElementById('onetrust-reject-all-handler');
  const allowAllButton = document.getElementById('accept-recommended-btn-handler');
  const rejectAllButtons = document.getElementsByClassName('ot-pc-refuse-all-handler');
  const confirmMyChoicesButtons = document.getElementsByClassName(
    'save-preference-btn-handler onetrust-close-btn-handler'
  );

  const handleCookieBannerClick = (e: any) => {
    const buttonText = e.target.innerText;
    setCookieButtonName(buttonText);
  };

  useEffect(() => {
    if (!loading && loaded) {
      return;
    }
    const { cookieCompliance } = getConfigValue('cookieBanners').oneTrust;
    const { scriptUrl, scriptId } = cookieCompliance;

    if (!IS_BROWSER || !scriptUrl || !scriptId) {
      return;
    }

    const existing = document.querySelectorAll(`script#${ONE_TRUST_SDK_SCRIPT_ID}`);

    if (existing.length > 0) {
      setLoading(false);
      setLoaded(true);
      return;
    }

    const handleLoad = () => {
      setLoading(false);
      setLoaded(true);

      // OptanonWrapper function is required by One Trust script which is called after the SDK is loaded
      window.OptanonWrapper = function () {
        acceptAllCookiesButton?.addEventListener('click', handleCookieBannerClick);
        rejectAllButton?.addEventListener('click', handleCookieBannerClick);
        allowAllButton?.addEventListener('click', handleCookieBannerClick);
        rejectAllButtons[0]?.addEventListener('click', handleCookieBannerClick);
        confirmMyChoicesButtons[0]?.addEventListener('click', handleCookieBannerClick);
      };
    };

    const handleError = (err: ErrorState) => {
      setLoading(false);
      setLoaded(false);
      setError(err);
    };

    // Add autoblock script
    const blockScript = document.createElement('script');
    blockScript.setAttribute('type', 'text/javascript');
    blockScript.setAttribute('src', getAutoBlockScript(scriptId));
    document.head.appendChild(blockScript);

    const otSdkScript = document.createElement('script');

    otSdkScript.setAttribute('src', scriptUrl);
    otSdkScript.setAttribute('data-domain-script', scriptId);
    otSdkScript.setAttribute('id', ONE_TRUST_SDK_SCRIPT_ID);
    otSdkScript.setAttribute('type', 'text/javascript');
    otSdkScript.setAttribute('charset', 'UTF-8');
    otSdkScript.addEventListener('load', handleLoad);
    otSdkScript.addEventListener('error', handleError);

    document.head.appendChild(otSdkScript);

    return () => {
      document.head.removeChild(blockScript);
      document.head.removeChild(otSdkScript);
      otSdkScript.removeEventListener('load', handleLoad);
      otSdkScript.removeEventListener('error', handleError);

      // Remove event listeners when component unmounts
      acceptAllCookiesButton?.removeEventListener('click', handleCookieBannerClick);
      rejectAllButton?.removeEventListener('click', handleCookieBannerClick);
      allowAllButton?.removeEventListener('click', handleCookieBannerClick);
      rejectAllButtons[0]?.removeEventListener('click', handleCookieBannerClick);
      confirmMyChoicesButtons[0]?.removeEventListener('click', handleCookieBannerClick);
    };
  }, [
    acceptAllCookiesButton,
    rejectAllButton,
    allowAllButton,
    rejectAllButtons,
    confirmMyChoicesButtons,
    loading,
    loaded,
  ]);

  useEffect(() => {
    if (cookieButtonName) {
      setTimeout(() => {
        trackEvent({
          name: CustomEventNames.BUTTON_CLICK,
          type: EventTypes.Other,
          attributes: {
            Name: cookieButtonName,
          },
        });
      }, 1000);
    }
  }, [cookieButtonName, trackEvent]);

  return { loaded, loading, error };
}
